import React, { useEffect, useMemo, useState } from "react";
import {
  DealQuestionnaireQuestionSettings,
  DealsQuestionnaireData,
  Responses,
  Score
} from "@/pages/deals/types";
import YesOrNoQuestion from "@/components/controls/Questions/YesOrNoQuestion";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import { ScoreType } from "@/pages/deals/scoreConfig";
import { usePeople, usePeopleByCompany } from "@/entities/people/hooks";
import { useDeals } from "@/pages/deals/hooks";
import { PersonData } from "@/entities/people/types";
import AddApproversModal from "@/components/layouts/AddApproversModal";
import { Button } from "@/components/ui/button";
import { Add } from "@dreamhub/designsystem/icons";
import { groupBy, isEmpty, map } from "lodash";
import { removeQuestionById } from "@/pages/deals/helpers";
import { cn } from "@/utils/utils";
import StakeholdersApprovalEmptyState from "./StakeholdersApprovalEmptyState";
import { stakeholdersApprovalLabels } from "./stakeholdersApprovalLabels";
import { renderSectionTitle } from "@/utils/questionRendering/questionRenderer";
import { useQuestionnaireResponses } from "@/hooks/useQuestionnaireResponses";

interface Props {
  dealId: string;
  questionnaire: DealsQuestionnaireData;
  handleUpdateQuestionnaireScore: (newValue: Score | null) => void;
  responses?: Responses;
  relatedQuestionnaire?: DealsQuestionnaireData;
  handleRefetch?: () => Promise<void>;
  onOpenRelatedQuestionnaire?: () => void;
  disabled?: boolean;
  addApprovalButtonDisabled?: boolean;
}

const SimpleQuestionnaire: React.FC<Props> = ({
  dealId,
  questionnaire,
  handleUpdateQuestionnaireScore,
  responses,
  relatedQuestionnaire,
  handleRefetch,
  onOpenRelatedQuestionnaire,
  disabled = false,
  addApprovalButtonDisabled = false
}) => {
  const { t, i18n } = useTranslation();
  const {
    kpiValue,
    questions: questionnaireQuestions,
    id: questionnaireId
  } = questionnaire;

  const [questions, setQuestions] = useState(questionnaireQuestions);

  const isStakeholdersApproval = kpiValue === ScoreType.STAKEHOLDERS_APPROVAL;

  const hasSections = questions?.some((question) => !isEmpty(question.section));

  const {
    entities: [dealData]
  } = useDeals([dealId || ""]);

  const companyContactIds = usePeopleByCompany(dealData?.companyId || "").data
    ?.ids;
  const companyPeople = usePeople(companyContactIds || []).entities;

  const relatedQuestionsMap = useMemo(() => {
    if (!isStakeholdersApproval) return;
    const peopleMap = companyPeople.reduce(
      (map, person) => {
        if (person) {
          map[person.id] = person;
        }
        return map;
      },
      {} as Record<string, PersonData>
    );

    return (relatedQuestionnaire?.questions || []).reduce(
      (map, question) => {
        const response = relatedQuestionnaire?.responses?.[question.id] || null;
        const personId = response?.response?.value;
        map[question.id] = {
          ...question,
          person: typeof personId === "string" ? peopleMap[personId] : null
        };
        return map;
      },
      {} as Record<
        string,
        DealQuestionnaireQuestionSettings & {
          person: PersonData | null;
        }
      >
    );
  }, [relatedQuestionnaire, companyPeople, isStakeholdersApproval]);

  const relatedPeopleIds = useMemo(() => {
    if (!relatedQuestionsMap) return [];

    const customApprovers = map(Object.values(responses || {}), (response) =>
      response?.response?.person ? response.response?.person.id : ""
    ).filter(Boolean);

    const relatedPeople = Object.values(relatedQuestionsMap)
      .filter((question) => question.person !== null)
      .map((question) => question.person?.id)
      .filter((id): id is string => id !== undefined);
    return [...relatedPeople, ...customApprovers];
  }, [relatedQuestionsMap]);

  const { localResponses, updateResponse, resetResponses } =
    useQuestionnaireResponses(responses);
  const [isAddApproversModalOpen, setIsAddApproversModalOpen] = useState(false);

  useEffect(() => {
    setQuestions(questionnaireQuestions);
  }, [questionnaireQuestions]);

  const sortedQuestions = questions
    ? [...questions].sort(
        (a, b) => (a.questionOrder ?? 0) - (b.questionOrder ?? 0)
      )
    : [];
  const renderQuestion = (
    question: DealQuestionnaireQuestionSettings,
    questionClassName?: string
  ) => (
    <YesOrNoQuestion
      key={question.id}
      question={question}
      questionnaireId={questionnaireId}
      dealId={dealId}
      handleUpdateQuestionnaireScore={handleUpdateQuestionnaireScore}
      localResponses={localResponses}
      updateResponse={updateResponse}
      onResponseError={resetResponses}
      options={
        isStakeholdersApproval ? [t("approved"), t("declined")] : undefined
      }
      hideTooltip={isStakeholdersApproval}
      relatedQuestion={
        isStakeholdersApproval
          ? relatedQuestionsMap?.[question?.relatedQuestionId || ""]
          : undefined
      }
      enableDelete={isStakeholdersApproval && !disabled}
      updateQuestionsState={(questionId: string) => {
        setQuestions?.((prevQuestions) =>
          removeQuestionById(prevQuestions, questionId)
        );
      }}
      resetQuestionnaire={handleRefetch}
      questionClassName={cn(
        questionClassName,
        isStakeholdersApproval && "mx-0 gap-1"
      )}
      className={
        isStakeholdersApproval
          ? "flex-row justify-between items-center"
          : "pb-8"
      }
      questionVariant={isStakeholdersApproval ? "body" : undefined}
      disabled={disabled}
    />
  );

  if (hasSections && questions) {
    const groupedQuestions = groupBy(sortedQuestions, "section");

    return (
      <div className="flex flex-col gap-6">
        {Object.entries(groupedQuestions).map(([section, sectionQuestions]) => (
          <div
            key={section}
            className="flex flex-col gap-6 border-b border-border-border1"
          >
            <Text variant="label">{renderSectionTitle(i18n, section, t)}</Text>
            <div>
              {sectionQuestions.map((question) => renderQuestion(question))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-1",
        isStakeholdersApproval && "p-2 gap-8"
      )}
    >
      {isStakeholdersApproval && sortedQuestions.length === 0 ? (
        <StakeholdersApprovalEmptyState
          onOpenRelatedQuestionnaire={onOpenRelatedQuestionnaire || (() => {})}
          labels={stakeholdersApprovalLabels(t, relatedQuestionnaire?.name)}
        />
      ) : (
        <>
          <div
            className={cn(
              "inline",
              isStakeholdersApproval && "flex flex-col gap-8"
            )}
          >
            {sortedQuestions.map((question) => renderQuestion(question))}
          </div>
          {isStakeholdersApproval && (
            <Button
              className="w-fit"
              variant="tertiary"
              onClick={() => setIsAddApproversModalOpen(true)}
              disabled={addApprovalButtonDisabled}
            >
              <Add />
              {t("deals.dealView.addApproval")}
            </Button>
          )}
        </>
      )}
      <AddApproversModal
        isOpen={isAddApproversModalOpen}
        setIsOpen={setIsAddApproversModalOpen}
        entityId={dealId}
        companyId={dealData?.companyId || ""}
        approverPeopleIds={relatedPeopleIds}
        dealId={dealId}
        questionnaireId={questionnaireId}
        resetQuestionnaire={handleRefetch}
      />
    </div>
  );
};

export default SimpleQuestionnaire;
